import { Controller } from "@hotwired/stimulus"

/**
 * Controller for handling the category selection in the navbar search form.
 * This controller manages the state and behavior of the category selection buttons,
 * updates the search form's query container based on the selected category,
 * toggles the visibility of the package category container, and updates the placeholder
 * values for the query input field.
 */

export default class extends Controller {
  static targets = ["button"]
  static outlets = ["navbar-search-form", "navbar-search-form--placeholder"]
  static values = { queryInputPlaceholder: Object }

  selectCategory(event) {
    this.navbarSearchFormOutlet.searchCategoryInputTarget.value = event.params.categoryValue

    this.toggleSearchCategoryButtons(event)
    this.updateQueryContainer(event)
    this.toggleLocationTypeContainerPresence(event)
    this.toggleGuestsContainerPresence(event)
    this.togglePackageCategoryContainerPresence(event)
    this.updatePlaceholderValues()

    if (event.params.categoryValue != "venue") {
      this.navbarSearchFormOutlet.packageCategoryInputTarget.value = ""
    }
  }

  // Toggles the active class on the search category buttons
  toggleSearchCategoryButtons(event) {
    const selectedButton = this.buttonTargets.find(button => button.dataset["navbarSearchForm-CategorySelectorCategoryValueParam"] == event.params.categoryValue)

    this.buttonTargets.forEach(button => {
      button.classList.remove("btn-primary")
      button.classList.remove("btn-light")
      button.classList.add(button == selectedButton ? "btn-primary" : "btn-light")
    })
  }

  // Updating the class on the query container allows us to style the search form differently based on the selected category
  updateQueryContainer(event) {
    this.navbarSearchFormOutlet.queryTarget.classList.remove("border", "border-danger")
    this.navbarSearchFormOutlet.packageCategoryInputTarget.classList.remove("border", "border-danger")
    this.navbarSearchFormOutlet.queryContainerTarget.classList.remove("venue")
    this.navbarSearchFormOutlet.queryContainerTarget.classList.remove("meal")
    this.navbarSearchFormOutlet.queryContainerTarget.classList.remove("activity")
    this.navbarSearchFormOutlet.queryContainerTarget.classList.add(event.params.categoryValue)

    this.navbarSearchFormPlaceholderOutlet.queryContainerTarget.classList.remove("venue")
    this.navbarSearchFormPlaceholderOutlet.queryContainerTarget.classList.remove("meal")
    this.navbarSearchFormPlaceholderOutlet.queryContainerTarget.classList.remove("activity")
    this.navbarSearchFormPlaceholderOutlet.queryContainerTarget.classList.add(event.params.categoryValue)
  }

  // If the selected category is "venue", we show the package category container, otherwise we hide it
  togglePackageCategoryContainerPresence(event) {
    if (event.params.categoryValue == "venue") {
      this.navbarSearchFormOutlet.packageCategoryDividerTarget.classList.remove("d-none")
      this.navbarSearchFormOutlet.packageCategoryContainerTarget.classList.remove("d-none")

      this.navbarSearchFormPlaceholderOutlet.packageCategoryDividerTarget.classList.remove("d-none")
      this.navbarSearchFormPlaceholderOutlet.packageCategoryContainerTarget.classList.remove("d-none")

      this.navbarSearchFormOutlet.keywordsContainerTarget.classList.add("d-none")
      this.navbarSearchFormPlaceholderOutlet.keywordsContainerTarget.classList.add("d-none")
    } else {
      if (event.params.categoryValue == "activity") {
        this.navbarSearchFormOutlet.packageCategoryDividerTarget.classList.remove("d-none")
        this.navbarSearchFormOutlet.keywordsContainerTarget.classList.remove("d-none")

        this.navbarSearchFormPlaceholderOutlet.packageCategoryDividerTarget.classList.remove("d-none")
        this.navbarSearchFormPlaceholderOutlet.keywordsContainerTarget.classList.remove("d-none")
      } else {
        this.navbarSearchFormOutlet.keywordsContainerTarget.classList.add("d-none")
        this.navbarSearchFormOutlet.packageCategoryDividerTarget.classList.add("d-none")

        this.navbarSearchFormPlaceholderOutlet.keywordsContainerTarget.classList.add("d-none")
        this.navbarSearchFormPlaceholderOutlet.packageCategoryDividerTarget.classList.add("d-none")
      }
      this.navbarSearchFormOutlet.packageCategoryContainerTarget.classList.add("d-none")

      this.navbarSearchFormPlaceholderOutlet.packageCategoryContainerTarget.classList.add("d-none")

      this.navbarSearchFormOutlet.bedroomsCountWrapperTarget?.classList.add("d-none")
      this.navbarSearchFormOutlet.navbarSearchFormPlaceholderOutlet?.bedroomsContainerTarget?.classList.add("d-none")
    }
  }

  toggleLocationTypeContainerPresence(event) {
    if (event.params.categoryValue == "activity") {
      this.navbarSearchFormOutlet.locationTypeContainerTarget.classList.remove("d-none")
      this.navbarSearchFormOutlet.locationTypeDividerTarget.classList.remove("d-none")
      this.navbarSearchFormPlaceholderOutlet.locationTypeDividerTarget.classList.remove("d-none")
      this.navbarSearchFormPlaceholderOutlet.locationTypeContainerTarget.classList.remove("d-none")
    } else {
      this.navbarSearchFormOutlet.locationTypeContainerTarget.classList.add("d-none")
      this.navbarSearchFormOutlet.locationTypeDividerTarget.classList.add("d-none")
      this.navbarSearchFormPlaceholderOutlet.locationTypeDividerTarget.classList.add("d-none")
      this.navbarSearchFormPlaceholderOutlet.locationTypeContainerTarget.classList.add("d-none")
    }
  }

  toggleGuestsContainerPresence(event) {
    if (event.params.categoryValue == "activity") {
      this.navbarSearchFormOutlet.guestsWrapperTarget.classList.add("d-none")
      this.navbarSearchFormOutlet.guestDividerTarget.classList.add("d-none")
      this.navbarSearchFormPlaceholderOutlet.guestDividerTarget.classList.add("d-none")
      this.navbarSearchFormPlaceholderOutlet.guestsContainerTarget.classList.add("d-none")
    } else {
      this.navbarSearchFormOutlet.guestsWrapperTarget.classList.remove("d-none")
      this.navbarSearchFormOutlet.guestDividerTarget.classList.remove("d-none")
      this.navbarSearchFormPlaceholderOutlet.guestDividerTarget.classList.remove("d-none")
      this.navbarSearchFormPlaceholderOutlet.guestDividerTarget.classList.remove("d-none")
      this.navbarSearchFormPlaceholderOutlet.guestsContainerTarget.classList.remove("d-none")
    }
  }

  // Update the placeholder value for the query input based on the selected category
  updatePlaceholderValues() {
    this.navbarSearchFormOutlet.queryTarget.placeholder = this.queryInputPlaceholderValue[event.params.categoryValue]
    this.navbarSearchFormOutlet.updatePlaceholderValues()
  }
}
